import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Heart,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Mail,
  RefreshCw,
  Users
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const ThankYouGenerator = ({ isOpen, onClose, campaignId, donors = [] }) => {
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedTone, setSelectedTone] = useState('heartfelt');
  const [mode, setMode] = useState('single'); // single or bulk
  
  // Form fields for single generation
  const [donorName, setDonorName] = useState('');
  const [donationAmount, setDonationAmount] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(true);

  const tones = [
    { id: 'heartfelt', name: 'Heartfelt', emoji: '💝' },
    { id: 'professional', name: 'Professional', emoji: '💼' },
    { id: 'casual', name: 'Casual', emoji: '😊' }
  ];

  const generateSingle = async () => {
    if (!donorName || !donationAmount) return;
    
    setLoading(true);
    try {
      const data = await aiAPI.generateThankYouMessage(
        campaignId,
        donorName,
        parseFloat(donationAmount),
        isFirstTime,
        selectedTone
      );
      setResult(data);
    } catch (err) {
      console.error('Failed to generate message:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateBulk = async () => {
    setBulkLoading(true);
    try {
      const data = await aiAPI.generateBulkThankYouMessages(campaignId);
      setBulkResults(data.messages);
    } catch (err) {
      console.error('Failed to generate bulk messages:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const copyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-background border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-400" />
            AI Thank You Message Generator
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create personalized thank you messages for your generous donors
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-card rounded-lg">
            <button
              onClick={() => setMode('single')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'single'
                  ? 'bg-emerald-500 text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Single Message
            </button>
            <button
              onClick={() => setMode('bulk')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'bulk'
                  ? 'bg-emerald-500 text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Bulk Generate
            </button>
          </div>

          {/* Tone Selector */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground mr-2">Tone:</span>
            {tones.map(tone => (
              <button
                key={tone.id}
                onClick={() => setSelectedTone(tone.id)}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  selectedTone === tone.id
                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/50'
                    : 'bg-card text-muted-foreground border border-border hover:border-border'
                }`}
              >
                {tone.emoji} {tone.name}
              </button>
            ))}
          </div>

          {mode === 'single' ? (
            /* Single Message Generation */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Donor Name</label>
                  <Input
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="John Smith"
                    className="bg-card border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Donation Amount ($)</label>
                  <Input
                    type="number"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    placeholder="100"
                    className="bg-card border-border"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="firstTime"
                  checked={isFirstTime}
                  onChange={(e) => setIsFirstTime(e.target.checked)}
                  className="rounded border-border"
                />
                <label htmlFor="firstTime" className="text-sm text-muted-foreground">
                  First-time donor
                </label>
              </div>

              <Button
                onClick={generateSingle}
                disabled={loading || !donorName || !donationAmount}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate Thank You Message</>
                )}
              </Button>

              {result && (
                <div className="space-y-3 p-4 rounded-xl bg-card/50 border border-border">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Subject Line</label>
                    <div className="p-3 bg-card rounded-lg text-foreground text-sm">
                      {result.subject_line}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Message</label>
                    <Textarea
                      value={result.message}
                      readOnly
                      className="min-h-[150px] bg-card border-border text-foreground"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => copyMessage(`Subject: ${result.subject_line}\n\n${result.message}`)}
                      className="flex-1 border-border"
                    >
                      {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copied!' : 'Copy Message'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={generateSingle}
                      className="border-border"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Bulk Generation */
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-card/50 border border-border">
                <p className="text-muted-foreground text-sm">
                  Generate personalized thank you messages for all your campaign donors automatically.
                  Messages will be tailored based on each donor&apos;s name and contribution amount.
                </p>
              </div>

              <Button
                onClick={generateBulk}
                disabled={bulkLoading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {bulkLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating messages...</>
                ) : (
                  <><Users className="w-4 h-4 mr-2" /> Generate for All Donors</>
                )}
              </Button>

              {bulkResults && (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  <p className="text-sm text-muted-foreground">
                    Generated {bulkResults.length} messages
                  </p>
                  {bulkResults.map((item, index) => (
                    <div key={index} className="p-4 rounded-xl bg-card/50 border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-foreground font-medium">{item.donor_name}</span>
                        <span className="text-emerald-400">${item.amount}</span>
                      </div>
                      {item.error ? (
                        <p className="text-red-400 text-sm">{item.error}</p>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground mb-1">Subject: {item.subject_line}</p>
                          <p className="text-foreground/80 text-sm line-clamp-3">{item.message}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyMessage(`Subject: ${item.subject_line}\n\n${item.message}`)}
                            className="mt-2 text-xs"
                          >
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouGenerator;
